///
/// EarthBrowser
///
/// Created by Matt Giger on 12/3/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { spherical } from "@/earthbrowser/utils/spherical"
import type { Camera } from "@stovokor/camera/Camera"
import { CameraState } from "@stovokor/camera/CameraState"
import type { Perspective } from "@stovokor/camera/Perspective"
import type { NodeBase } from "@stovokor/engine/NodeBase"
import { clamp, cross, matrix, normal, range, spring, trig, vec3 } from "@stovokor/math"

const cMovementSpringDamping = 30
const cZoomSpringDamping = 15
const cMinZoomRadius = 10
const cInitialZoomRadius = 500
const cPaddedEarthRadius = wgs.a + 10.0
const cSurfaceClearanceKm = 1.05

/**
 * Camera parameters for entity-relative animation
 */
export class EntityCameraParams {
	radius: number
	theta: number
	phi: number

	constructor(radius: number | spherical, theta?: number, phi?: number) {
		if (radius instanceof spherical) {
			this.radius = radius.radius
			this.theta = radius.theta
			this.phi = radius.phi
		} else {
			this.radius = radius
			this.theta = theta ?? trig.pi
			this.phi = phi ?? trig.half_pi / 6
		}
	}

	get array(): number[] {
		return [this.radius, this.theta, this.phi]
	}

	get spherical(): spherical {
		return new spherical(this.radius, this.theta, this.phi)
	}
}

/**
 * EntityCameraState - Manages camera state and animation for viewing a moving entity
 *
 * This class encapsulates:
 * - Camera position in spherical coordinates relative to a target entity
 * - Spring animation for smooth transitions
 * - Projection updates that track the moving target
 *
 * Similar to EarthCameraState but designed for orbiting around a moving object
 * (e.g., satellite, aircraft) rather than Earth's center.
 */
export class EntityCameraState {
	public context: EarthBrowserContext

	/** The target node being orbited */
	public target: NodeBase

	/** Current camera reference */
	public camera?: Camera

	/** Camera position in spherical coordinates relative to the target */
	public relativeSpherical: spherical

	/** Valid elevation range for camera distance from target */
	public readonly elevationRange: range

	/** Spring animation controller */
	private spring: spring

	/** Whether this state is currently controlling the camera */
	public isActive = false

	/** Flag to prevent feedback loop when updating from user input */
	private skipNextReduxUpdate = false

	constructor(
		context: EarthBrowserContext,
		target: NodeBase,
		maxZoomRadius: number,
		initialSpherical: spherical = new spherical(cInitialZoomRadius, trig.pi, trig.half_pi / 6)
	) {
		this.context = context
		this.target = target
		this.elevationRange = new range(cMinZoomRadius, maxZoomRadius)
		this.relativeSpherical = initialSpherical

		// Set up spring animation
		this.spring = new spring()
			.damping(cMovementSpringDamping)
			.tolerance([1, 0.001, 0.001])
			.onUpdate(this.onSpringUpdate.bind(this))
			.onComplete(this.onSpringComplete.bind(this))
	}

	// ========== Camera Control ==========

	/**
	 * The instance currently controlling the camera (undefined when Earth
	 * or nothing is focused). Lets the CameraDirector orbit the focused
	 * entity without plumbing through every feature-node class.
	 */
	public static active?: EntityCameraState

	/**
	 * Called when entity gains focus - set up camera
	 */
	public activate(camera: Camera): void {
		this.camera = camera
		this.isActive = true
		EntityCameraState.active = this
		this.spring.stop()
		camera.state.stopAnimating()

		// The camera position has already been set by the focus transition
		// Just sync our internal state from the current camera position
		this.syncFromCamera()

		this.updateProjection()
	}

	/**
	 * Called when entity loses focus
	 */
	public deactivate(): void {
		this.spring.stop()
		this.camera?.state.stopAnimating()
		this.isActive = false
		if (EntityCameraState.active === this) {
			EntityCameraState.active = undefined
		}
	}

	/**
	 * Advance the orbit angle by deltaTheta radians (camera director idle
	 * orbit). Writes through the normal projection path so the camera keeps
	 * tracking the moving target.
	 */
	public orbitBy(deltaTheta: number): void {
		this.spring.stop()
		this.relativeSpherical = new spherical(
			this.relativeSpherical.radius,
			this.relativeSpherical.theta + deltaTheta,
			this.relativeSpherical.phi
		)
		this.updateProjection()
	}

	/**
	 * Get current camera parameters (accounts for running spring)
	 */
	public getCurrentParams(): EntityCameraParams {
		if (this.spring.running) {
			const current = this.spring.current
			return new EntityCameraParams(current[0], current[1], current[2])
		}
		return new EntityCameraParams(this.relativeSpherical)
	}
	/**
	 * Animate the tilt and bearing to the given values
	 * Converts geographic bearing to local theta coordinate
	 */
	public animatedTiltAndBearing(tilt: number, bearing: number): void {
		const newTilt = clamp(tilt, 0, this.maxPhi(this.relativeSpherical.radius) - 0.001)
		// Convert geographic bearing to local theta: theta = π - bearing
		const targetTheta = trig.pi - bearing
		const newParams = new EntityCameraParams(this.relativeSpherical.radius, targetTheta, newTilt)
		this.animateTo(newParams)
	}

	/**
	 * Animate only the bearing (theta) to a new value
	 * Converts geographic bearing to local theta coordinate
	 * In the entity's local frame: theta = π points north (bearing = 0)
	 */
	public animatedCameraBearing(bearing: number): void {
		// Convert geographic bearing to local theta
		// theta = π corresponds to north (bearing = 0)
		// The relationship is: theta = π - bearing
		const targetTheta = trig.pi - bearing

		const newParams = new EntityCameraParams(
			this.relativeSpherical.radius,
			targetTheta,
			this.relativeSpherical.phi
		)
		this.animateTo(newParams)
	}

	/**
	 * Animate only the tilt (phi) to a new value
	 */
	public animatedCameraTilt(tilt: number): void {
		const constrainedTilt = clamp(tilt, 0, this.maxPhi(this.relativeSpherical.radius) - 0.001)
		const newParams = new EntityCameraParams(
			this.relativeSpherical.radius,
			this.relativeSpherical.theta,
			constrainedTilt
		)
		this.animateTo(newParams)
	}

	/**
	 * Immediately set tilt without animation (for direct user input like sliders)
	 * Skips Redux update to prevent feedback loop
	 */
	public immediateCameraTilt(tilt: number): void {
		const constrainedTilt = clamp(tilt, 0, this.maxPhi(this.relativeSpherical.radius) - 0.001)
		this.relativeSpherical = new spherical(
			this.relativeSpherical.radius,
			this.relativeSpherical.theta,
			constrainedTilt
		)
		this.spring.stop()
		this.skipNextReduxUpdate = true
		this.updateProjection()
	}

	/**
	 * Immediately set bearing without animation (for direct user input like sliders)
	 * Converts geographic bearing to local theta coordinate
	 * Skips Redux update to prevent feedback loop
	 */
	public immediateCameraBearing(bearing: number): void {
		// Convert geographic bearing to local theta: theta = π - bearing
		const targetTheta = trig.pi - bearing
		this.relativeSpherical = new spherical(
			this.relativeSpherical.radius,
			targetTheta,
			this.relativeSpherical.phi
		)
		this.spring.stop()
		this.skipNextReduxUpdate = true
		this.updateProjection()
	}

	/**
	 * Create new camera parameters
	 */
	public createParams(radius: number, theta: number, phi: number): EntityCameraParams {
		return new EntityCameraParams(radius, theta, phi)
	}

	/**
	 * Animate to new camera parameters
	 */
	public animateTo(params: EntityCameraParams, damping: number = cMovementSpringDamping): void {
		const currentParams = this.getCurrentParams()
		this.spring
			.damping(damping)
			.from(currentParams.array)
			.sendTo(params.array)
	}

	/**
	 * Animate to new parameters with zoom damping
	 */
	public animateToWithZoomDamping(params: EntityCameraParams): void {
		this.animateTo(params, cZoomSpringDamping)
	}

	/**
	 * Apply zoom delta (for wheel events)
	 */
	public applyZoomDelta(delta: number): boolean {
		const current = this.getCurrentParams()
		const unclamped = current.radius + delta
		const radius = this.elevationRange.clamp(unclamped)

		// Return false if we've hit the max range (allows fallback to viewport zoom)
		if (delta > 0 && unclamped >= this.elevationRange.max) {
			return false
		}

		const newParams = this.createParams(radius, current.theta, current.phi)
		this.animateTo(newParams, cZoomSpringDamping)
		return true
	}

	/**
	 * Get maximum phi angle where camera won't intersect Earth
	 */
	public maxPhi(radius?: number): number {
		const D = this.target.transform.position.length
		const r = radius ?? this.relativeSpherical.radius
		if (D - r > cPaddedEarthRadius) {
			return trig.pi
		} else if (D < cPaddedEarthRadius) {
			return trig.half_pi
		} else {
			return trig.pi - Math.asin(cPaddedEarthRadius / D)
		}
	}

	/**
	 * Called every frame to update camera position
	 */
	public renderUpdate(): void {
		this.updateProjection()
	}

	/**
	 * Calculate bearing and tilt from camera vectors
	 * Bearing: angle from north (clockwise) in the local tangent plane
	 * Tilt: angle from nadir (looking straight down) toward horizon
	 */
	private calculateBearingAndTilt(position: vec3, forward: vec3, up?: vec3): { bearing: number; tilt: number } {
		// Get geographic location for the camera
		const geo = new geographic(position)

		// Create rotation matrix to transform from world to local tangent plane
		const xRot = matrix.axisRotation(vec3.unitX, geo.latitude - trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, geo.longitude - trig.pi)
		const worldToLocal = yRot.invert().multiply(xRot.invert())

		// Transform forward vector to local tangent plane
		const localForward = worldToLocal.multVec3(forward, 0)

		// Calculate bearing (angle from north, clockwise)
		// In local coordinates: +X is east, +Y is up, +Z is north
		//
		// For a roll-free camera the forward and screen-up vectors project onto
		// the same compass azimuth, but their horizontal magnitudes trade off
		// with tilt: looking near-nadir, forward is almost vertical and its
		// tiny horizontal lean is numerical noise (using it would return an
		// arbitrary bearing and visibly spin the view during focus
		// transitions), while screen-up is nearly horizontal and stable.
		// Looking near the horizon it's the reverse. Use whichever vector
		// carries the larger horizontal component.
		const forwardHorizontal = Math.hypot(localForward.x, localForward.z)
		let bearing = Math.atan2(localForward.x, localForward.z)
		if (up) {
			const localUp = worldToLocal.multVec3(up, 0)
			if (Math.hypot(localUp.x, localUp.z) > forwardHorizontal) {
				bearing = Math.atan2(localUp.x, localUp.z)
			}
		}

		// Calculate tilt (angle from down/nadir to forward)
		// When looking straight down, tilt is 0; when looking at horizon, tilt is π/2
		const toEarthCenter = normal(position).multiply(-1)
		const dotProduct = Math.max(-1, Math.min(1, forward.x * toEarthCenter.x + forward.y * toEarthCenter.y + forward.z * toEarthCenter.z))
		const tilt = Math.acos(dotProduct)

		return { bearing, tilt }
	}

	/**
	 * Save current camera state to Redux including calculated bearing and tilt
	 */
	public saveCameraState(position: vec3, forward: vec3): void {
		// Skip Redux update if we're responding to user slider input
		// This prevents feedback loops
		if (this.skipNextReduxUpdate) {
			this.skipNextReduxUpdate = false
			return
		}

		const { bearing, tilt } = this.calculateBearingAndTilt(position, forward)
		const cameraState = engineBridge.getState()?.earthbrowser.cameraState
		if (!cameraState) return
		engineBridge.dispatch({
			type: engineActions.setCameraState,
			payload: {
				radius: cameraState.radius,
				theta: cameraState.theta,
				phi: cameraState.phi,
				tilt: tilt,
				bearing: bearing
			}
		})
	}

	/**
	 * Get the target camera state for focus transitions
	 * Returns the camera position/orientation relative to the current target position
	 *
	 * The destination preserves the user's current view orientation: the same
	 * compass bearing and tilt from nadir, re-aimed at the target from
	 * focusDistance away. A north-up top-down "2D" view therefore zooms
	 * straight in and stays north-up instead of adopting whatever oblique
	 * angle the raw target-to-camera direction happened to have.
	 *
	 * The orientation is computed using the same local-frame (Y-up) convention as
	 * updateProjection(). This is important: when the focus transition completes,
	 * activate() immediately re-derives the orientation via updateProjection(). If the
	 * transition target used a different orientation (e.g. preserving the incoming
	 * camera's roll), the camera would snap to the steady-state orientation on the final
	 * frame. Matching the conventions here keeps the handoff seamless.
	 *
	 * @param camera Optional camera to use for calculating destination. If not provided, uses this.camera
	 */
	public getTargetCameraState(camera?: Camera): CameraState {
		const cameraToUse = camera ?? this.camera
		const targetPosition = this.target.transform.position
		const targetTransform = this.calculateTargetTransform(targetPosition)

		if (!cameraToUse) {
			// Fallback if camera not set yet
			const relativePosition = this.relativeSpherical.cartesian

			const forward = normal(relativePosition).multiply(-1)
			const right = cross(forward, vec3.unitY)
			const up = normal(cross(right, forward))

			const position = targetTransform.multVec3(relativePosition, 1)
			const worldForward = targetTransform.multVec3(forward, 0)
			const worldUp = targetTransform.multVec3(up, 0)

			return new CameraState(position, worldForward, worldUp)
		}

		// Preserve the current view orientation: derive the camera's compass
		// bearing and tilt from nadir, then place the destination at
		// focusDistance from the target along that same view direction
		const desiredDistance = this.relativeSpherical.radius
		const { bearing, tilt } = this.calculateBearingAndTilt(
			cameraToUse.position,
			cameraToUse.state.forward,
			cameraToUse.state.up
		)

		// Same bearing/tilt -> local spherical mapping as animatedTiltAndBearing()
		const maxPhiConstraint = this.maxPhi(desiredDistance)
		const phi = clamp(tilt, 0.001, maxPhiConstraint - 0.001)
		const theta = trig.pi - bearing
		const relativePosition = new spherical(desiredDistance, theta, phi).cartesian
		const destinationPosition = targetTransform.multVec3(relativePosition, 1)

		// Orient using the same local-frame convention as updateProjection() so the
		// steady-state camera control does not snap the orientation when the transition ends.
		const forward = normal(relativePosition).multiply(-1)
		const right = cross(forward, vec3.unitY)
		const up = normal(cross(right, forward))

		const worldForward = targetTransform.multVec3(forward, 0)
		const worldUp = targetTransform.multVec3(up, 0)

		return new CameraState(destinationPosition, worldForward, worldUp)
	}

	// ========== Sync Methods ==========

	/**
	 * Sync internal state from the current camera position.
	 * Call this when you want to adopt the camera's current position
	 * (e.g., when transitioning from another focus with preserveCurrentState).
	 */
	public syncFromCamera(): void {
		if (!this.camera) return

		// Calculate camera position relative to target
		const targetPosition = this.target.transform.position
		const targetTransform = this.calculateTargetTransform(targetPosition)
		const inverseTransform = targetTransform.invert()
		const relativePosition = inverseTransform.multVec3(this.camera.position, 1)

		// Convert to spherical and constrain phi
		const currentRelativeSpherical = new spherical(relativePosition)
		const maxPhiConstraint = this.maxPhi(currentRelativeSpherical.radius)
		const phi = Math.max(0.001, Math.min(maxPhiConstraint - 0.001, currentRelativeSpherical.phi))
		const radius = this.elevationRange.clamp(currentRelativeSpherical.radius)

		this.relativeSpherical = new spherical(radius, currentRelativeSpherical.theta, phi)
	}

	// ========== Private Methods ==========

	/**
	 * Calculate the transform matrix for positioning relative to target
	 */
	private calculateTargetTransform(targetPosition: vec3): matrix {
		const geo = new geographic(targetPosition)
		const translation = matrix.translate(targetPosition)
		const xRot = matrix.axisRotation(vec3.unitX, geo.latitude - trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, geo.longitude - trig.pi)
		return xRot.multiply(yRot).multiply(translation)
	}

	/**
	 * Update camera projection and position
	 */
	private updateProjection(): void {
		if (!this.camera) return

		// Don't update projection during camera state animations (focus transitions)
		if (this.camera.state.animating) {
			// Always update bearing and tilt in Redux, even during animations
			this.saveCameraState(this.camera.position, this.camera.state.forward)
			return
		}

		const targetPosition = this.target.transform.position
		const relativePosition = this.relativeSpherical.cartesian
		const targetTransform = this.calculateTargetTransform(targetPosition)

		const clamped = this.clampToLocalSurface(targetTransform, relativePosition)

		// Calculate camera orientation (in local frame)
		const forward = normal(clamped.relativePosition).multiply(-1)
		const right = cross(forward, vec3.unitY)
		const up = normal(cross(right, forward))

		// Transform to world space
		const position = clamped.position
		const worldForward = targetTransform.multVec3(forward, 0)
		const worldUp = targetTransform.multVec3(up, 0)

		// Update camera state
		const state = this.camera.state
		state.position = position
		state.forward = worldForward
		state.up = worldUp

		// Update bearing and tilt in Redux using the *final* camera vectors
		this.saveCameraState(state.position, state.forward)

		// Update projection to maximize depth resolution
		const elevation = position.length - wgs.a
		const perspective = this.camera.projection as Perspective
		perspective.near = elevation < 30 ? 1 : 10

		perspective.far = clamped.relativePosition.length + wgs.a
	}

	/**
	 * Prevent the camera from going below the local surface (ellipsoid + rough elevation).
	 *
	 * This is a last-line safeguard that works even if phi constraints are imperfect due to
	 * local-frame alignment or elevated terrain.
	 */
	private clampToLocalSurface(
		targetTransform: matrix,
		relativePosition: vec3
	): { position: vec3; relativePosition: vec3 } {
		const position = targetTransform.multVec3(relativePosition, 1)
		const minRadius = this.minSurfaceRadiusKmAtWorldPosition(position)

		if (position.length >= minRadius) {
			return { position, relativePosition }
		}

		// Reproject outward along the radial normal.
		// IMPORTANT: do NOT overwrite `this.relativeSpherical` here.
		// If we do, we can "fight" user rotation and end up stuck at the clamp boundary.
		const correctedWorldPosition = normal(position).multiply(minRadius)
		const inverseTransform = targetTransform.invert()
		const correctedRelativePosition = inverseTransform.multVec3(correctedWorldPosition, 1)

		return { position: correctedWorldPosition, relativePosition: correctedRelativePosition }
	}

	private minSurfaceRadiusKmAtWorldPosition(position: vec3): number {
		const geo = new geographic(position)
		return wgs.a + this.context.displayElevationKm(geo) + cSurfaceClearanceKm
	}

	private onSpringUpdate(values: number[]): void {
		// Restrict the phi angle to the maximum angle where camera won't intersect Earth
		const maxPhi = this.maxPhi(values[0])
		const phi = Math.min(values[2], maxPhi)

		this.relativeSpherical = new spherical(values[0], values[1], phi)
		this.updateProjection()
	}

	private onSpringComplete(): void {
		// Normalize theta to [0, 2π] when spring completes
		let theta = this.relativeSpherical.theta % trig.two_pi
		if (theta < 0) {
			theta += trig.two_pi
		}
		this.relativeSpherical = new spherical(this.relativeSpherical.radius, theta, this.relativeSpherical.phi)
	}
}

